// One-time "Get the app" card for iPhone users on the website, shown right
// after a value moment (lineup submitted, league joined) - never on landing,
// never as an interstitial. Renders nothing for the native app, non-iOS,
// dismissed/snoozed visitors, or when VITE_APP_STORE_ID is unset. Pages that need to
// choose between this and another nudge should branch on
// shouldShowAppPrompt() themselves rather than relying on this null.
import React, { useEffect, useState } from 'react';
import { mixpanelTrack } from '@/services/mixpanel';
import { APP_STORE_URL, dismissAppPromptForever, shouldShowAppPrompt, snoozeAppPrompt } from '@/utils/appPrompt';

type Trigger = 'lineup_submitted' | 'league_joined';

const COPY: Record<Trigger, { title: string; body: string }> = {
  lineup_submitted: {
    title: 'Get live scores on your phone',
    body: 'Lineup alerts, starting confirmations, and live scoring - in the Fantasy Futbol app.',
  },
  league_joined: {
    title: 'Run your team from the app',
    body: 'Waiver results, trade offers, and starting confirmations as push notifications.',
  },
};

interface Props {
  trigger: Trigger;
  className?: string;
}

const GetAppPrompt: React.FC<Props> = ({ trigger, className = '' }) => {
  const [visible, setVisible] = useState<boolean>(() => shouldShowAppPrompt());

  useEffect(() => {
    if (visible) mixpanelTrack('App Prompt Shown', { Trigger: trigger });
  }, [visible, trigger]);

  if (!visible) return null;
  const copy = COPY[trigger];

  // The primary CTA is a real anchor, not window.open: in-app browsers
  // (Instagram, Facebook, Gmail) route anchor navigations through the
  // webview's navigation-policy delegate, which is the path they reliably
  // intercept for apps.apple.com; window.open is a silent no-op in many
  // WKWebView hosts. Hand-off VERIFIED on a real iPhone 2026-08-28 from
  // inside the Instagram in-app browser, so a tap is a permanent dismiss.
  const onGetApp = () => {
    mixpanelTrack('App Prompt Clicked', { Trigger: trigger });
    dismissAppPromptForever();
    // Keep the anchor mounted until its default navigation has been dispatched,
    // then hide the card in the original tab so it is gone when the user returns.
    window.setTimeout(() => setVisible(false), 0);
  };

  const onNotNow = () => {
    mixpanelTrack('App Prompt Dismissed', { Trigger: trigger });
    snoozeAppPrompt();
    setVisible(false);
  };

  return (
    <div
      role="region"
      aria-label="Get the Fantasy Futbol app"
      className={`bg-surface-card border border-accent-yellow/40 rounded-lg p-4 mb-4 ${className}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-heading font-bold text-accent-yellow">{copy.title}</p>
          <p className="text-xs text-gray-400 mt-0.5">{copy.body}</p>
        </div>
        <button
          type="button"
          onClick={onNotNow}
          aria-label="Dismiss app prompt"
          className="inline-flex min-h-10 min-w-10 shrink-0 items-center justify-center rounded-lg text-lg leading-none text-gray-500 transition-colors duration-150 hover:text-gray-300 active:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow"
        >
          &times;
        </button>
      </div>
      <div className="mt-3 flex gap-2">
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onGetApp}
          className="inline-flex min-h-10 items-center justify-center rounded-lg bg-accent-yellow px-4 py-2 font-heading text-sm font-bold text-primary-dark transition-colors duration-150 hover:bg-accent-yellow/80 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-light-blue focus-visible:ring-offset-2 focus-visible:ring-offset-surface-card"
        >
          Get the app
        </a>
        <button
          type="button"
          onClick={onNotNow}
          className="inline-flex min-h-10 items-center justify-center rounded-lg px-3 py-2 text-sm text-gray-400 transition-colors duration-150 hover:text-gray-200 active:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow"
        >
          Not now
        </button>
      </div>
    </div>
  );
};

export default GetAppPrompt;
